import {
    BarChartOutlined,
    CalendarOutlined,
    BankOutlined,
    FileTextOutlined,
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    EyeOutlined,
    PieChartOutlined,
    TableOutlined,
    ReloadOutlined,
    FileExcelOutlined,
    FilePdfOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    Select,
    Skeleton,
    Tabs,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Tag,
    Tooltip,
    Empty,
    Divider,
    Progress,
    Modal,
    message,
    Dropdown,
    DatePicker
} from 'antd';
import frFR from "antd/locale/fr_FR";
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StatistiquesSAPStyle from './StatistiquesSAPStyle';
import {
    statistiquesGlobalesColumns,
    detailSinistresColumns,
    resumeParBeneficiaireColumns,
    repartitionExpandColumns
} from './StatistiquesSAPData';
import { DetailSinistreModal } from './DetailSinistreModal';
import { RepartitionChart } from './RepartitionChart';
import PoolGumVtEtat from './PoolGumVtEtat';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import axiosInstance from '../../../../variables/api/axiosInstance';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import Footer from '../../../../components/Footer';

import { exportSAPToExcel, exportSAPToPDF, printSAPReport } from './StatistiquesSAPExport';

const PRIMARY = '#242166';

const StatistiquesSAP = () => {
    // États pour les filtres
    const [exercices, setExercices] = useState([]);
    const [selectedExercice, setSelectedExercice] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    // Date d'arrêté pour l'état Pool « SAP GUM & VT » (défaut = aujourd'hui)
    // Défaut = null → SAP COURANT (persisté). Renseigner une date ne sert qu'à RECONSTRUIRE le SAP
    // à une date passée. On ne met plus dayjs() par défaut : « arrêté = aujourd'hui » basculait en
    // reconstruction (≈ évaluation) au lieu de lire le SAP persisté, faussant l'affichage courant.
    const [arreteAu, setArreteAu] = useState(null);

    // Branche SAP déduite de la catégorie sélectionnée (le code GUM peut être 'GUM' ou 'MO'
    // = « GUM-MOTO » ; VT = 'VT'/'VC'/'VL'). Fallback sur le libellé.
    const brancheSAP = (() => {
        const code = (selectedCategorie || '').toUpperCase();
        if (!code) return null;
        if (['VT', 'VC', 'VL'].includes(code)) return 'VT';
        if (['GUM', 'MO'].includes(code)) return 'GUM';
        const catObj = categories.find((c) => c.codeCategorie === selectedCategorie);
        const nom = (catObj?.nom || '').toUpperCase();
        if (nom.includes('GUM')) return 'GUM';
        if (nom.includes('TRANSIT')) return 'VT';
        return null;
    })();

    // Contexte POOL / branche GUM-VT → on affiche l'état croisé dédié à la place des onglets.
    const orgSel = compagnies.find((c) => c.code === selectedCompagnie);
    const isPoolContext =
        (orgSel && (orgSel.typeOrganisme === 'POOL' || orgSel.code === '00')) ||
        brancheSAP != null;

    // États pour les données
    const [loading, setLoading] = useState(false);
    const [statistiquesGlobales, setStatistiquesGlobales] = useState([]);
    const [detailSinistres, setDetailSinistres] = useState([]);
    const [resumeParBeneficiaire, setResumeParBeneficiaire] = useState([]);

    // États pour les modals
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedSinistre, setSelectedSinistre] = useState(null);

    // Onglet actif
    const [activeTab, setActiveTab] = useState("1");

    // Chargement initial — 3 appels INDÉPENDANTS : si l'un échoue (ex. la fonction SAP des
    // exercices), les autres dropdowns se remplissent quand même (avant : un seul Promise.all
    // bloquait tout au moindre échec).
    useEffect(() => {
        // Exercices (état SAP)
        axiosInstance.get('/api/stats/exercices')
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                console.log('exercices', data);
                setExercices(data);
                if (data.length > 0) {
                    setSelectedExercice(data[0].exercice); // le plus récent
                }
            })
            .catch((err) => {
                console.error('Erreur exercices SAP', err);
                toast.error("Erreur lors du chargement des exercices.");
            });

        // Catégories
        axiosInstance.get('/api/categorie')
            .then((res) => {
                const catData = Array.isArray(res.data) ? res.data : [];
                const excludedCodes = ["A", "B", "T"];
                const filtered = catData.filter(
                    (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
                );
                const hasTPVCategories = catData.some((c) => excludedCodes.includes(c.codeCategorie));
                if (hasTPVCategories) {
                    filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
                }
                // Le filtre « Catégorie » est en réalité un filtre de BRANCHE : on ajoute VT
                // (branche des VC/VL) pour pouvoir filtrer GUM ou VT dans l'état Pool.
                const hasVT = catData.some((c) => ["VC", "VL", "VT"].includes(c.codeCategorie));
                if (hasVT) {
                    filtered.push({ codeCategorie: "VT", nom: "VT (Véhicule de Transit)", synthetic: true });
                }
                setCategories(filtered);
            })
            .catch((err) => console.error('Erreur catégories', err));

        // Organismes (assurances + Pool) : filtre « Organisme cédant »
        axiosInstance.get('/api/organisme/all/assurances-pool')
            .then((res) => setCompagnies(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('Erreur organismes', err));
    }, []);

    // Charger les statistiques quand les filtres changent
    useEffect(() => {
        if (selectedExercice) {
            chargerStatistiques();
        }
        // arreteAu inclus : changer la date d'arrêté recharge l'état SAP reconstruit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedExercice, selectedCategorie, selectedCompagnie, arreteAu]);

    const chargerStatistiques = async () => {
        setLoading(true);
        try {
            const params = {
                exercice: selectedExercice,
                categorieCode: selectedCategorie || undefined,
                compagnieCode: selectedCompagnie || undefined
            };
            // Date d'arrêté : SAP RECONSTRUIT à cette date pour l'état principal.
            // Défaut = aujourd'hui → le backend retombe sur le SAP courant (pas de régression).
            const paramsStats = {
                ...params,
                arreteAu: arreteAu ? dayjs(arreteAu).format('YYYY-MM-DD') : undefined
            };

            const arreteParam = arreteAu ? dayjs(arreteAu).format('YYYY-MM-DD') : undefined;
            const [statsRes, detailRes, resumeRes] = await Promise.all([
                axiosInstance.get('/api/stats', { params: paramsStats }),
                // Détail et par-bénéficiaire alignés sur la MÊME date d'arrêté → tout l'écran/PDF cohérent.
                axiosInstance.get('/api/stats/detail', { params: { ...params, arreteAu: arreteParam } }),
                axiosInstance.get('/api/stats/par-beneficiaire', {
                    params: {
                        exercice: selectedExercice,
                        categorieCode: selectedCategorie,
                        arreteAu: arreteParam
                    }
                })
            ]);
            console.log('statsRes.data', statsRes.data);
            setStatistiquesGlobales(statsRes.data);
            console.log('detailRes.data', detailRes.data);
            setDetailSinistres(detailRes.data);
            console.log('resumeRes.data', resumeRes.data);
            setResumeParBeneficiaire(resumeRes.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des statistiques");
        } finally {
            setLoading(false);
        }
    };

    // Calculs des totaux
    const calculerTotaux = () => {
        const totaux = {
            nombreSinistres: 0,
            montantSinistres: 0,
            montantEvaluation: 0,
            montantSap: 0,
            montantReglement: 0
        };

        statistiquesGlobales.forEach(stat => {
            totaux.nombreSinistres += stat.nombreSinistres || 0;
            totaux.montantEvaluation += stat.montantTotalEvaluation || 0;
            totaux.montantSap += stat.montantTotalSap || 0;
            // Réglé = évaluation − SAP (SAP = solde restant à payer).
            totaux.montantReglement += (stat.montantTotalEvaluation - stat.montantTotalSap) || 0;
        });

        return totaux;
    };

    const totaux = calculerTotaux();

    // Handlers pour l'export
    const handleExportExcel = () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à exporter');
            return;
        }

        message.loading('Génération du fichier Excel...', 1);

        try {
            exportSAPToExcel(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                }
            );
            message.success('Export Excel généré avec succès');
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de la génération du fichier Excel');
        }
    };

    const handleExportPDF = async () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à exporter');
            return;
        }

        message.loading('Génération du fichier PDF...', 1);

        try {
            await exportSAPToPDF(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                },
                totaux
            );
            message.success('Export PDF généré avec succès');
        } catch (error) {
            console.error('Erreur export PDF:', error);
            message.error('Erreur lors de la génération du fichier PDF');
        }
    };

    const handlePrint = async () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à imprimer');
            return;
        }

        try {
            await printSAPReport(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                },
                totaux
            );
        } catch (error) {
            console.error('Erreur impression:', error);
            message.error('Erreur lors de la préparation de l\'impression');
        }
    };

    // Menu dropdown pour l'export
    const exportMenuItems = [
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
            onClick: handleExportExcel
        },
        {
            key: 'pdf',
            label: 'Exporter en PDF',
            icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
            onClick: handleExportPDF
        },
        {
            type: 'divider'
        },
        {
            key: 'print',
            label: 'Imprimer',
            icon: <PrinterOutlined />,
            onClick: handlePrint
        }
    ];

    // Extraction des compagnies bénéficiaires
    const getCompagniesBeneficiaires = () => {
        const compagniesSet = new Map();

        statistiquesGlobales.forEach(stat => {
            stat.repartitionParBeneficiaire?.forEach(rep => {
                if (!compagniesSet.has(rep.beneficiaireCode)) {
                    compagniesSet.set(rep.beneficiaireCode, {
                        code: rep.beneficiaireCode,
                        nom: rep.beneficiaireNom,
                        taux: rep.tauxRepartition
                    });
                }
            });
        });

        return Array.from(compagniesSet.values()).sort((a, b) => a.code.localeCompare(b.code));
    };

    const compagniesBeneficiaires = getCompagniesBeneficiaires();

    // Handlers
    const handleVoirDetail = (record) => {
        console.log('recordSinistre', record);
        setSelectedSinistre(record);
        setDetailModalVisible(true);
    };

    const resetFilters = () => {
        setSelectedCategorie(null);
        setSelectedCompagnie(null);
    };

    // Summary du tableau « Par branche » : totaux Nb + SAP (sans colonnes bénéficiaires).
    const generateStatistiquesSummary = (pageData) => {
        let totalSinistres = 0;
        let totalSap = 0;
        pageData.forEach(record => {
            totalSinistres += record.nombreSinistres || 0;
            totalSap += record.montantTotalSap || 0;
        });
        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#f7f7fb' }}>
                    {/* colSpan 4 = colonne d'expand + Exercice + Branche + Compagnie cédante */}
                    <Table.Summary.Cell index={0} colSpan={4}>
                        <strong style={{ fontSize: 13 }}>TOTAL ({pageData.length} ligne(s))</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="center">
                        <strong style={{ color: '#2563eb', fontSize: 14 }}>{totalSinistres}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                        <strong style={{ color: '#dc2626', fontSize: 14, whiteSpace: 'nowrap' }}>{displayNumbers(totalSap)} F</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} />
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <StatistiquesSAPStyle />

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f0f2f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* Header */}
                            <div className="sap-page-header">
                                <div className="header-content">
                                    <div>
                                        <h1>
                                            <BarChartOutlined /> Statistiques SAP / Organisme
                                        </h1>
                                        <p>
                                            États statistiques des Sinistres À Payer par exercice, organisme et branche
                                        </p>
                                    </div>
                                    {/* Export/impression standard masqués en contexte Pool (l'état croisé
                                        a ses propres boutons Excel/PDF). */}
                                    {!isPoolContext && (
                                        <Space>
                                            <Dropdown
                                                menu={{ items: exportMenuItems }}
                                                placement="bottomRight"
                                            >
                                                <Button
                                                    type="primary"
                                                    icon={<DownloadOutlined />}
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                >
                                                    Exporter
                                                </Button>
                                            </Dropdown>
                                            <Button
                                                icon={<PrinterOutlined />}
                                                onClick={handlePrint}
                                            >
                                                Imprimer
                                            </Button>
                                        </Space>
                                    )}
                                </div>
                            </div>

                            {/* Filtres */}
                            <Card className="filters-card" style={{ marginBottom: 24 }}>
                                <Row gutter={[16, 16]} align="middle">
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><CalendarOutlined /> Exercice</label>
                                            <Select
                                                placeholder="Sélectionner un exercice"
                                                style={{ width: '100%' }}
                                                value={selectedExercice}
                                                onChange={setSelectedExercice}
                                                options={exercices.map(ex => ({
                                                    value: ex.exercice,
                                                    label: (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{ex.exercice}</span>
                                                            <Tag color="blue">{ex.nombreSinistres} sinistres</Tag>
                                                        </div>
                                                    )
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><FileTextOutlined /> Catégorie</label>
                                            <Select
                                                placeholder="Toutes les catégories"
                                                style={{ width: '100%' }}
                                                value={selectedCategorie}
                                                onChange={setSelectedCategorie}
                                                allowClear
                                                options={categories.map(cat => ({
                                                    value: cat.codeCategorie,
                                                    label: cat.nom
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><BankOutlined /> Organisme</label>
                                            <Select
                                                placeholder="Tous les organismes"
                                                style={{ width: '100%' }}
                                                value={selectedCompagnie}
                                                onChange={setSelectedCompagnie}
                                                allowClear
                                                options={compagnies.map(comp => ({
                                                    value: comp.code,
                                                    label: comp.raisonSociale
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    {isPoolContext && (
                                        <Col xs={24} sm={12} md={6}>
                                            <div className="filter-item">
                                                <label><CalendarOutlined /> Arrêté au (vide = courant)</label>
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    value={arreteAu}
                                                    onChange={(d) => setArreteAu(d || null)}
                                                    format="DD/MM/YYYY"
                                                    placeholder="Aujourd'hui (SAP courant)"
                                                    allowClear
                                                />
                                            </div>
                                        </Col>
                                    )}
                                    <Col xs={24} sm={12} md={6}>
                                        <Space style={{ marginTop: 22 }}>
                                            <Button
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={chargerStatistiques}
                                                loading={loading}
                                            >
                                                Rechercher
                                            </Button>
                                            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                                                Réinitialiser
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {isPoolContext ? (
                                <PoolGumVtEtat
                                    arreteAu={arreteAu}
                                    branche={brancheSAP}
                                />
                            ) : (
                            <>
                            {/* Cartes de résumé */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Nombre de sinistres"
                                            value={totaux.nombreSinistres}
                                            prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Total sinistres payés"
                                            value={displayNumbers(totaux.montantReglement)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#ff4d4f' }} />}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Card>
                                </Col>
                                {/* <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Total recours"
                                            value={displayNumbers(totaux.montantRecours)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#52c41a' }} />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col> */}
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card stat-card-highlight">
                                        <Statistic
                                            title="Montant SAP"
                                            value={displayNumbers(totaux.montantSap)}
                                            suffix="F"
                                            prefix={<PieChartOutlined style={{ color: '#722ed1' }} />}
                                            valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Répartition par compagnie bénéficiaire */}
                            {/* {compagniesBeneficiaires.length > 0 && (
                                <Card
                                    title={
                                        <Space>
                                            <PieChartOutlined />
                                            <span>Répartition par compagnie bénéficiaire</span>
                                        </Space>
                                    }
                                    style={{ marginBottom: 24 }}
                                >
                                    <Row gutter={[16, 16]}>
                                        {compagniesBeneficiaires.map(comp => {
                                            const totalBenef = resumeParBeneficiaire
                                                .filter(r => r.beneficiaireCode === comp.code)
                                                .reduce((sum, r) => sum + (r.montantNetRepartis || 0), 0);

                                            const pourcentage = totaux.montantNet > 0
                                                ? (totalBenef / totaux.montantNet) * 100
                                                : 0;

                                            return (
                                                <Col key={comp.code} xs={24} sm={12} md={8} lg={6}>
                                                    <div className="beneficiaire-card">
                                                        <div className="beneficiaire-header">
                                                            <BankOutlined />
                                                            <span>{comp.nom}</span>
                                                        </div>
                                                        <div className="beneficiaire-taux">
                                                            Taux: {(comp.taux || 0).toFixed(2)}%
                                                        </div>
                                                        <div className="beneficiaire-montant">
                                                            {displayNumbers(totalBenef)} F
                                                        </div>
                                                        <Progress
                                                            percent={pourcentage}
                                                            showInfo={false}
                                                            strokeColor="#722ed1"
                                                            size="small"
                                                        />
                                                        <div className="beneficiaire-percent">
                                                            {pourcentage.toFixed(1)}% du total
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card>
                            )} */}

                            {/* Onglets principaux */}
                            <Card>
                                <ConfigProvider locale={frFR}>
                                    <Skeleton loading={loading} active>
                                        <Tabs
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            items={[

                                                {
                                                    label: (
                                                        <span>
                                                            <FileTextOutlined /> Détail des sinistres
                                                        </span>
                                                    ),
                                                    key: "1",
                                                    children: (
                                                        <Table
                                                            columns={detailSinistresColumns(compagniesBeneficiaires, handleVoirDetail)}
                                                            dataSource={detailSinistres}
                                                            rowKey="sinistreCodeId"
                                                            pagination={{
                                                                pageSize: 20,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} sinistre(s)`
                                                            }}
                                                            scroll={{ x: 'max-content' }}
                                                            size="small"
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <BankOutlined /> Par bénéficiaire
                                                        </span>
                                                    ),
                                                    key: "2",
                                                    children: (
                                                        <Table
                                                            columns={resumeParBeneficiaireColumns()}
                                                            dataSource={resumeParBeneficiaire}
                                                            rowKey={(record) => `${record.exercice}-${record.beneficiaireCode}-${record.categorieCode}`}
                                                            pagination={{
                                                                pageSize: 10,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} ligne(s)`
                                                            }}
                                                            scroll={{ x: 'max-content' }}
                                                            size="small"
                                                            expandable={{
                                                                expandedRowRender: (record) => (
                                                                    <div style={{ padding: 16, background: '#fafafa' }}>
                                                                        <h4>Détail par compagnie cédante:</h4>
                                                                        <Row gutter={[16, 16]}>
                                                                            {record.detailParCedante?.map(detail => (
                                                                                <Col key={detail.cedanteCode} span={8}>
                                                                                    <Card size="small">
                                                                                        <div><strong>{detail.cedanteNom}</strong></div>
                                                                                        <div>Sinistres: {detail.nombreSinistres}</div>
                                                                                        {/* <div>Taux: {displayNumbers(detail.tauxRepartition)} %</div> */}
                                                                                        <div>SAP: {displayNumbers(detail.montantSAP)} F</div>
                                                                                    </Card>
                                                                                </Col>
                                                                            ))}
                                                                        </Row>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <TableOutlined /> Par branche
                                                        </span>
                                                    ),
                                                    key: "3",
                                                    children: (
                                                        <Table
                                                            columns={statistiquesGlobalesColumns()}
                                                            dataSource={statistiquesGlobales}
                                                            rowKey={(record) => `${record.exercice}-${record.compagnieCedanteCode}-${record.categorieCode}`}
                                                            pagination={{
                                                                pageSize: 12,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} ligne(s)`
                                                            }}
                                                            scroll={{ x: 'max-content' }}
                                                            size="small"
                                                            summary={(pageData) => generateStatistiquesSummary(pageData)}
                                                            expandable={{
                                                                rowExpandable: (record) => (record.repartitionParBeneficiaire?.length || 0) > 0,
                                                                expandedRowRender: (record) => (
                                                                    <div style={{ padding: '8px 12px', background: '#f7f7fb' }}>
                                                                        <div style={{ fontWeight: 600, marginBottom: 8, color: PRIMARY }}>
                                                                            Répartition par bénéficiaire
                                                                        </div>
                                                                        <Table
                                                                            columns={repartitionExpandColumns()}
                                                                            dataSource={record.repartitionParBeneficiaire || []}
                                                                            rowKey={(r) => r.beneficiaireCode}
                                                                            pagination={false}
                                                                            size="small"
                                                                            bordered
                                                                        />
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <PieChartOutlined /> Graphiques
                                                        </span>
                                                    ),
                                                    key: "4",
                                                    children: (
                                                        <RepartitionChart
                                                            data={resumeParBeneficiaire}
                                                            compagniesBeneficiaires={compagniesBeneficiaires}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Skeleton>
                                </ConfigProvider>
                            </Card>
                            </>
                            )}
                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal détail sinistre */}
            <DetailSinistreModal
                visible={detailModalVisible}
                onClose={() => setDetailModalVisible(false)}
                sinistre={selectedSinistre}
                compagniesBeneficiaires={compagniesBeneficiaires}
            />
        </div>
    );
};

export default StatistiquesSAP;